import { RecurrentFeathersModel } from './RecurrentFeathersModel'
import { DateUtils } from '../services/date-utils.service'
import { addMinutes, differenceInMinutes } from 'date-fns'
import { AssistanceTemplate } from './AssistanceTemplate'

export type EditorAssistanceAssignment = {
  id: number
  statusId: number
  fullname: string
  assistantId: number
  transitDurationMinutes: number
}

export type AssistanceJournalEntry = {
  content: string
  assistantId?: number
  authorUserId?: number
}

export class EditorAssistance extends RecurrentFeathersModel {
  constructor(public id: number | null) {
    super(id)
  }
  static serviceName = 'editor-assistances'

  // TODO: URID
  statusId: number
  clientId: number
  clientName: string
  plannedFrom: Date
  plannedTo: Date
  templatePlannedTimeFrom: Date
  templatePlannedTimeTo: Date
  realizedFrom: Date
  realizedTo: Date
  addressStart: string
  addressEnd: string
  keyCoordinatorId: number
  keyCoordinatorName: string
  shortDescription: string
  detailedDescription: string
  coordinatorNote: string
  isSleepover: boolean
  preferredAssistantGenderId: number
  strongAssistantNeeded: boolean
  advancedAssistantNeeded: boolean
  isNotToBePrimarilyAssigned: boolean
  isLateOrder: boolean
  isPaidInternally: boolean
  nonClientWork: boolean
  primaryContactContent: string
  isSentToClient: boolean
  isShowInExchange: boolean
  isBilled = false
  assignments: EditorAssistanceAssignment[] = []
  rrule: string
  bundleParentId: number
  allocationId: number
  journalEntries: AssistanceJournalEntry[] = []

  updateFromProps(props: Partial<EditorAssistance>): this {
    const ea = super.updateFromProps(props)
    if (ea.plannedFrom) ea.plannedFrom = new Date(ea.plannedFrom)
    if (ea.plannedTo) ea.plannedTo = new Date(ea.plannedTo)
    if (ea.realizedFrom) ea.realizedFrom = new Date(ea.realizedFrom)
    if (ea.realizedTo) ea.realizedTo = new Date(ea.realizedTo)

    if (!ea.journalEntries) ea.journalEntries = []

    return ea
  }

  static fromTemplate(template: AssistanceTemplate, statusId: number) {
    const a = new EditorAssistance(null)

    const plannedTimeFromHMS = DateUtils.hhColonMmToHourMinuteSecondObject(template.plannedTimeFrom)
    const durationMinutes = DateUtils.diffHHColonMMInMinutes(template.plannedTimeFrom, template.plannedTimeTo)

    a.statusId = statusId
    a.clientId = template.clientId
    a.clientName = template.client.fullname
    a.templatePlannedTimeFrom = new Date()
    a.templatePlannedTimeFrom.setHours(plannedTimeFromHMS.hour, plannedTimeFromHMS.minute, 0, 0)
    a.templatePlannedTimeTo = addMinutes(a.templatePlannedTimeFrom, durationMinutes)
    a.addressStart = template.addressStart
    a.addressEnd = template.addressEnd
    a.keyCoordinatorName = template.client.keyWorkerUser?.fullname
    a.shortDescription = template.shortDescription
    a.detailedDescription = template.detailedDescription
    a.coordinatorNote = template.coordinatorNote
    a.isSleepover = template.isSleepover
    a.isShowInExchange = template.isShowInExchange
    a.preferredAssistantGenderId = template.preferredGenderId
    a.strongAssistantNeeded = template.needsStrongAssistant
    a.advancedAssistantNeeded = template.needsAdvancedAssistant
    a.nonClientWork = template.nonClientWork
    a.isPaidInternally = template.isPaidInternally

    return a
  }

  /**
   * Creates an AssistanceTemplate based on the current instance.
   *
   * @returns AssistanceTemplate
   */
  toTemplate(): AssistanceTemplate {
    const template = new AssistanceTemplate()
    template.clientId = this.clientId
    template.client = null
    template.preferredGenderId = this.preferredAssistantGenderId
    template.shortDescription = this.shortDescription
    template.detailedDescription = this.detailedDescription
    template.plannedTimeFrom = this.plannedFrom ? DateUtils.dateToHhColonMm(this.plannedFrom) : undefined
    template.plannedTimeTo = this.plannedTo ? DateUtils.dateToHhColonMm(this.plannedTo) : undefined
    template.addressStart = this.addressStart
    template.addressEnd = this.addressEnd
    template.needsStrongAssistant = this.strongAssistantNeeded
    template.needsAdvancedAssistant = this.advancedAssistantNeeded
    template.coordinatorNote = this.coordinatorNote
    template.isSleepover = this.isSleepover
    template.isShowInExchange = this.isShowInExchange
    template.nonClientWork = this.nonClientWork
    template.isPaidInternally = this.isPaidInternally

    return template
  }

  generateFirstOccurrence() {
    const childAssistance = EditorAssistance.createFromProps(EditorAssistance, this)
    childAssistance.recurrenceParentId = null
    return childAssistance
  }

  generateOccurrence(occurrenceStartDate: Date, occurrenceEndDate?: Date) {
    const a = EditorAssistance.createFromProps(EditorAssistance, this)

    a.id = null
    a.plannedFrom = occurrenceStartDate
    a.plannedTo = occurrenceEndDate || addMinutes(occurrenceStartDate, this.durationMinutes)

    a.realizedFrom = null
    a.realizedTo = null

    a.recurrenceParentId = this.id
    a.recurrenceException = null
    a.recurrenceExceptionDate = new Date(a.plannedFrom)
    a.rrule = null

    a.isSentToClient = false
    a.isBilled = false

    return a
  }

  getDateFrom(): Date {
    return this.plannedFrom ? new Date(this.plannedFrom) : undefined
  }
  getDateTo(): Date {
    return this.plannedTo ? new Date(this.plannedTo) : undefined
  }
  getRealizedFrom(): Date {
    return this.realizedFrom ? new Date(this.realizedFrom) : undefined
  }
  getRealizedTo(): Date {
    return this.realizedTo ? new Date(this.realizedTo) : undefined
  }

  get durationMinutes() {
    return differenceInMinutes(this.getDateTo(), this.getDateFrom())
  }

  isBundleChild() {
    return !!this.bundleParentId
  }

  hasAssignments() {
    return !!this.assignments?.length
  }

  /**
   * Checks whether the assistance has not been saved in database yet, which means it's either:
   * - completely new assistance (nonrecurrent, recurrent parent, or assistance from template)
   * - expanded recurrent child
   */
  isNotSavedInDatabase() {
    return !this.isSavedInDatabase()
  }

  isSavedInDatabase() {
    return this.id || this.isRecurrent()
  }
}
